/**
 * What this device remembers about each daily table (DLY-004, and STA-001's source).
 *
 * One record per day, keyed by the SERVER's day key: the shots of the attempt that
 * counts, and, once cleared, the strokes it took. Shots, not positions: on reload they
 * are replayed through the same deterministic physics, so the table comes back exactly
 * as it was (docs/phase-3-plan.md §4.2). The first attempt is the one that counts
 * (D-029): a shot is saved the moment it is taken, before it plays out, so a reload
 * cannot undo a shot that is heading for a miss.
 *
 * `owner-only`, local (docs/access-patterns.md §4). This is the player's own record:
 * editing it fools only the person editing it (D-028), and nothing public will ever be
 * computed from it; Phase 4 scores come from shots the server replays itself (N3).
 * Still untrusted input on read (CLAUDE.md §10): every record is checked field by field
 * and a damaged one is dropped, never half-used.
 */
import { DEFAULT_PHYSICS, validateShot, type ShotInput } from '@potted/sim';
import type { KeyValueStore } from '../settings/settings.ts';

export const HISTORY_KEY = 'potted.history.v1';

export interface DayRecord {
  /** The server's day key, `YYYY-MM-DD`; also the level's id. */
  readonly dateKey: string;
  /** The physics the shots were played under. A different one cannot replay them. */
  readonly physicsVersion: number;
  readonly par: number;
  readonly ballCount: number;
  /** The counting attempt's shots, in order. */
  readonly shots: readonly ShotInput[];
  /** Strokes (shots plus penalties), set once the table is cleared. */
  readonly strokes?: number;
}

export interface History {
  get(dateKey: string): DayRecord | undefined;
  /** Every valid record, oldest first. */
  all(): readonly DayRecord[];
  put(record: DayRecord): void;
}

const DAY_KEY = /^(\d{4})-(\d{2})-(\d{2})$/;
/** Generous caps: anything beyond them is not a real game, and is dropped. */
const MAX_SHOTS = 300;
const MAX_STROKES = 1000;

function isDayKey(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const m = DAY_KEY.exec(value);
  if (m === null) return false;
  const ms = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return new Date(ms).toISOString().slice(0, 10) === value;
}

function wholeNumber(value: unknown, min: number, max: number): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= min && value <= max;
}

function readShot(raw: unknown): ShotInput | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined;
  const s = raw as Record<string, unknown>;
  const shot = {
    dx: s['dx'],
    dy: s['dy'],
    power: s['power'],
    spinX: s['spinX'],
    spinY: s['spinY'],
  };
  try {
    // The sim's own check: a shot it would refuse cannot be replayed, so it is not a record.
    validateShot(shot as ShotInput, DEFAULT_PHYSICS);
  } catch {
    return undefined;
  }
  return shot as ShotInput;
}

/** One record, checked field by field, or undefined if anything is wrong with it. */
export function readRecord(raw: unknown): DayRecord | undefined {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return undefined;
  const r = raw as Record<string, unknown>;
  if (!isDayKey(r['dateKey'])) return undefined;
  if (!wholeNumber(r['physicsVersion'], 1, 10_000)) return undefined;
  if (!wholeNumber(r['par'], 1, 30)) return undefined;
  if (!wholeNumber(r['ballCount'], 1, 15)) return undefined;
  if (!Array.isArray(r['shots']) || r['shots'].length > MAX_SHOTS) return undefined;
  const shots: ShotInput[] = [];
  for (const raw of r['shots'] as unknown[]) {
    const shot = readShot(raw);
    if (shot === undefined) return undefined;
    shots.push(shot);
  }
  const strokes = r['strokes'];
  if (strokes !== undefined && !wholeNumber(strokes, 1, MAX_STROKES)) return undefined;
  // Rebuilt, not passed through: nothing unexpected rides along.
  return {
    dateKey: r['dateKey'],
    physicsVersion: r['physicsVersion'],
    par: r['par'],
    ballCount: r['ballCount'],
    shots,
    ...(strokes === undefined ? {} : { strokes }),
  };
}

/** Every valid record in a stored blob; a damaged one is dropped, not the whole history. */
export function readHistory(text: string | null): Map<string, DayRecord> {
  const days = new Map<string, DayRecord>();
  if (text === null) return days;
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return days;
  }
  if (typeof parsed !== 'object' || parsed === null) return days;
  const stored = (parsed as Record<string, unknown>)['days'];
  if (typeof stored !== 'object' || stored === null || Array.isArray(stored)) return days;
  for (const [key, value] of Object.entries(stored)) {
    const record = readRecord(value);
    if (record !== undefined && record.dateKey === key) days.set(key, record);
  }
  return days;
}

/** The stored text, or null if there is none or storage refuses to be read. */
function readStored(storage: KeyValueStore | undefined): string | null {
  try {
    return storage?.getItem(HISTORY_KEY) ?? null;
  } catch {
    return null;
  }
}

/**
 * The device's history. Without storage (blocked site data, a private window that
 * throws), it still works for this page's lifetime, in memory, so a game is never
 * refused over it.
 */
export function createHistory(storage: KeyValueStore | undefined): History {
  const days = readHistory(readStored(storage));

  return {
    get: (dateKey) => days.get(dateKey),
    all: () => [...days.values()].sort((a, b) => (a.dateKey < b.dateKey ? -1 : 1)),
    put(record) {
      const valid = readRecord(record);
      if (valid === undefined)
        throw new Error(`history: refusing an invalid record for ${record.dateKey}`);
      days.set(valid.dateKey, valid);
      try {
        storage?.setItem(HISTORY_KEY, JSON.stringify({ days: Object.fromEntries(days) }));
      } catch {
        // Full or blocked: keep playing from memory. Losing it is the player's own loss.
      }
    },
  };
}
